import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Captions, Check, CaptionsOff, ChevronLeft, ChevronRight, CirclePause, FastForward, Loader2, Maximize2, Minimize2, PanelBottomClose, PanelRightClose, PanelRightOpen, Pause, Play, RotateCcw, Sparkles, Volume2, VolumeX } from "lucide-react";
import type { PremiumFeedbackItem } from "../api/resources";
import { cueStillFor, stepCue, CueStill } from "../live/cueRows";
import { SIDE_COLORS, sideColor } from "../pose/skeleton";
import { LiveAngleChart } from "./LiveAngleChart";
import type { MetricChartData } from "../api/types";
import {
  LivePoseFile,
  DwellMoment,
  framePoints,
  frameIndexAt,
  interpolatedFramePoints,
  alignPoseFileToFrameList,
  playbackGeometry,
  placementStyle,
  cropStyle,
  StageGeometry,
  SKELETON_BONES,
  LiveSeries,
  sampleAtIndex,
  seriesChart,
  captureFpsFor,
  liveSeriesOnCadence,
  activeStrikePulses,
  ribbonOutline,
  strideSeconds,
  pulseRing,
} from "../live/liveAnalysis";

/**
 * The LIVE free-analysis view (user reference clip, 2026-09-12): the original upload autoplays
 * with the skeleton drawn from the client-pose pose file; crossing a feedback moment can pause
 * the clip and fade that position's still in (a persisted preference — "pause or play straight
 * through" was the user's call to make, not ours); for run gait a live panel accumulates
 * cadence and knee-at-strike using the SAME chart card the session page renders at the bottom
 * (LiveAngleChart), so the live view and the metrics section read as one platform.
 *
 * Skeleton smoothness: keypoints are 10 fps but the clip renders at native rate, so each
 * landmark is interpolated between the bracketing pose file frames (../live/liveAnalysis,
 * tested) instead of stepping once per split frame — the jitter fix.
 */
/** One coaching row — the page's own rows (an overlay's headline text on a P-position frame). */
export interface LiveCoachingRow {
  pLabel: string;
  fileId: number;
  /** The cue: the overlay class (the renderer's segmentKey). */
  overlay: string;
  description: string;
  subText?: string;
  /** `${pLabel}:${overlay}` — one cue on one position (live/cueRows). */
  key?: string;
  /** The cue's readout cards ("Shaft 52°"), shown on its row. */
  values?: string[];
}

export interface LiveCoaching {
  summary?: string | null;
  total?: string | null;
  rows: LiveCoachingRow[];
}

export interface LiveFreeAnalysisProps {
  clipUrl: string;
  poseFile: LivePoseFile;
  moments: DwellMoment[];
  activity: string;
  /** Prefix for the moments' relative stillUrl values (the API base). */
  stillBase: string;
  /** Bottom-right stamp, the role the frame id plays on the rendered stills. */
  watermarkStamp?: string;
  /** The page's existing metric charts (the ones at the bottom of the session page) — shown
   * beside the player so the live view and the metrics section are one surface. */
  pageCharts?: MetricChartData[];
  /** The renderer's published placement + crop for this upload's stills (freeFeedback.stage).
   * Absent → the sport's known canvas, uncropped (older uploads look exactly as before). */
  stageGeometry?: StageGeometry | null;
  /** The page's coaching feedback (summary, total, rows) — shown under a Coaching tab beside
   * the Metrics tab (user, 2026-09-12: "merge the coaching feedback together with the charts
   * and have tab choices at the top"). */
  coaching?: LiveCoaching | null;
  /** The split window (freeFeedback.split): the pose file's time base. Frame i plays at
   * startSec + i/fps, and playback loops inside [startSec, min(stopSec, startSec + n/fps)) —
   * outside it there is no pose to draw (user, 2026-09-12: "near the end the pose lags").
   * `fps` is the upload's STORED capture cadence (the splitter's value) and beats the pose file's
   * own stamp; absent = the pose file's, then the legacy 10. */
  splitWindow?: { startSec: number; stopSec: number; fps?: number } | null;
  /** The upload's ordered split-frame ids (uploadFrames): a frame-keyed pose file is laid out over
   * them so frame i is split frame i even when the pose file skipped some. */
  orderedFrameIds?: number[];
  /** The C# analysis pass's live-series artifact (GET /uploads/liveSeries). Null = not produced
   * yet (404): the live metric panels hide; NOTHING is computed here instead. */
  liveSeries?: LiveSeries | null;
  /** One still per cue (freeFeedback.cueStills): the stage shows the selected cue's own picture, the
   * way the Pro video shows each cue on its own. Empty for uploads rendered before cue stills existed —
   * the stage then shows the position's composite, labelled as such. */
  cueStills?: CueStill[] | null;
  /** How long the clip holds on EACH cue when it pauses at a position by itself. */
  dwellMs?: number;
  /** The premium coaching's voice (premiumFeedback.items, clips on the uploads site at clipBase): at each position
   * the clip holds on each cue for its sentence and plays it. Null = no voice (free, or not out yet): dwellMs a cue. */
  voice?: { items: PremiumFeedbackItem[]; clipBase: string } | null;
  /** The premium coaching is on its way: a held position says its voice is coming. */
  voicePending?: boolean;
  /** The server has not written the positions' frames yet: the clip still stops at each position, with a loader. */
  framesPending?: boolean;
  /** Changes when the server re-renders the stills under the same names (premium after on-device), so the browser
   * fetches the new ones. */
  stillVersion?: string | null;
  /** A re-render is in progress (the page's loader covers the view): nothing plays — no clip, no stops, no voice —
   * until it lands (owner, 2026-10-13: "if its rendering dont play the clips or feedback"). */
  suspended?: boolean;
  /** Show this cue (a `${pLabel}:${overlay}` key) — sent from the coaching list below; `n` makes a repeat count. */
  requestCue?: { key: string; n: number } | null;
  /** The cue on show, for the coaching list below to follow; null when none. */
  onActiveCueChange?: (key: string | null) => void;
  /** One P position for the whole page (owner, 2026-10-21): show this position, as its chip would (`n` makes a repeat
   * ask count). Asked for the position already on show, it stays there. */
  requestPosition?: { label: string; n: number } | null;
  /** The position the view stopped at (a stop on its own, its chip, or a page request): the page follows it. */
  onPositionChange?: (label: string) => void;
  /** The narrated coaching video (premium). The player opens playing it, with Coaching first in the position row; a
   * position's pill leaves it for that position and Coaching goes back to it (owner, 2026-11-01: one view instead of
   * the Feedback and Analysis video tabs). */
  coachVideoUrl?: string | null;
  /** No coaching video (free): the Coaching pill is an upgrade page instead, on show for COACH_OFFER_MS when the
   * player opens, then the swing plays (owner, 2026-11-01: "a kinda upgrade ad page"). */
  coachOffer?: { headline: string; text: string; points: string[]; label: string; onClick: () => void } | null;
}

/** The stage height the full upgrade card needs (about 390 px of card and its margin); shorter, it goes compact. */
const OFFER_FULL_MIN_PX = 430;
/** How long a free analysis opens on the Coaching upgrade page before the swing plays. */
export const COACH_OFFER_MS = 5000;

// Platform palette (tailwind.config.js): the right side is the primary green and the left the
// accent violet — bones, joints, ankle trails, strike rings, joint series and tile eyebrows alike
// (user, 2026-09-12: "we should use better matching platform colors", not orange + blue;
// 2026-09-19: colour the pose per leg like the reference clip).
// Cross-body bones (the shoulders–hips box): neutral, so the two sides' colours carry the reading.
// The pose's colours are the skeleton drawing's (../pose/skeleton SIDE_COLORS): the upload page's
// analysis and the processing clip draw the same body in the same colours.
const JOINT = SIDE_COLORS.joint;
const TRAIL_LEFT = SIDE_COLORS.left;
const TRAIL_RIGHT = SIDE_COLORS.right;
/** The gait look the owner picked from the side-by-side (2026-09-19): the stride ribbon, at 1.15.
 * Strength multiplies the ribbon's width and glow, and nothing else — the strike pulse was already
 * settled and stays as it was (2026-09-20: "i prefer the older pulses"). PROVISIONAL — a look,
 * chosen by eye against upload 17820. */
const LOOK_STRENGTH = 1.15;
const TRAIL_HEAD_WIDTH = 26; // capture px at the foot, tapering to a point at the tail
const TRAIL_TAPER = 1.25;
const TRAIL_GLOW = 26;
const PULSE_CORE_RADIUS = 8; // the strike blob, in capture pixels


/** The viewer's mute for the coaching voice (owner, 2026-10-12), remembered per browser. */
const MUTE_KEY = "eleetz.liveFree.muted";
function loadMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "on";
  } catch {
    return false;
  }
}
/** The viewer's choice to hide the coaching captions (owner, 2026-10-12), remembered per browser. */
const CAPTIONS_KEY = "eleetz.liveFree.captions";
function loadCaptions(): boolean {
  try {
    return window.localStorage.getItem(CAPTIONS_KEY) !== "off";
  } catch {
    return true;
  }
}
/** The viewer folded the Coaching and Metrics panel away (owner, 2026-11-01: "make coaching + metrics collapseable"). */
const PANEL_KEY = "eleetz.liveFree.panel";
function loadPanelOpen(): boolean {
  try {
    return window.localStorage.getItem(PANEL_KEY) !== "closed";
  } catch {
    return true;
  }
}
/** The Coaching list follows the playback unless the viewer switched that off (owner, 2026-11-01: "it should be optional
 * also, but it should update as the p position feedback changes"). */
const FOLLOW_KEY = "eleetz.liveFree.follow";
function loadFollow(): boolean {
  try {
    return window.localStorage.getItem(FOLLOW_KEY) !== "off";
  } catch {
    return true;
  }
}
/** The golf swing's P-positions, address to finish. */
const GOLF_POSITIONS = ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "P9", "P10"];
// The panel (numbers, chart cursors) re-renders at a fixed 10 Hz whatever the upload's cadence:
// a component-wide React re-render per pose frame would not keep up at 60 fps, and the canvas
// (which does follow every frame) never waits for it.
const PANEL_TICK_HZ = 10;
// The ankle trail is a fixed span of TIME, so it covers the same stretch of running at any cadence.
const TRAIL_SECONDS = 4;

/** The renderer's watermark, replicated from BaseSetFinder.DrawBrandText so playback and the
 * burnt-in marks on the stills read as ONE surface: center wordmark 140px at 0x60 alpha, the
 * 45°-rotated pinstripe tile (34px, 0x0E alpha, 360/120 grid, brick offset), and the
 * bottom-right stamp (24px, 0x70 alpha, 60/20 px padding). SVG viewBox 1280x720 scales with
 * the stage exactly as the server's scaleFactor does. Hidden while a still shows — the still
 * carries its own marks. */
function RendererWatermark({ stamp, w, h }: { stamp?: string; w: number; h: number }) {
  // The server scales every size by min(w/1280, h/720) — DrawBrandText's scaleFactor.
  const k = Math.min(w / 1280, h / 720);
  const cx = w / 2;
  const cy = h / 2;
  const tiles: React.ReactNode[] = [];
  const diag = Math.sqrt(w * w + h * h);
  const stepX = 360 * k;
  const stepY = 120 * k;
  let row = 0;
  for (let y = cy - diag / 2; y <= cy + diag / 2; y += stepY, row++) {
    const rowOffset = row % 2 === 0 ? 0 : stepX / 2;
    for (let x = cx - diag / 2; x <= cx + diag / 2; x += stepX)
      tiles.push(
        <text key={`${row}-${x}`} x={x + rowOffset} y={y} fontSize={34 * k} textAnchor="middle">
          Eleetz.com
        </text>
      );
  }
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid="live-watermark"
      fill="#ffffff"
      fontFamily="Arial, sans-serif"
      fontWeight="bold"
    >
      <g opacity={0x0e / 255} transform={`rotate(-45 ${cx} ${cy})`}>{tiles}</g>
      <text x={cx} y={cy} fontSize={140 * k} textAnchor="middle" dominantBaseline="middle" opacity={0x60 / 255}>
        Eleetz.com
      </text>
      {stamp && (
        <text x={w - 60 * k} y={h - 20 * k} fontSize={24 * k} textAnchor="middle" opacity={0x70 / 255}>
          {stamp}
        </text>
      )}
    </svg>
  );
}

/** A compact metric tile in the platform's card language (white card, slate eyebrow) — the
 * reference product's tile grid, our palette. */
function Tile({ value, label, testid }: { value: string; label: string; testid?: string }) {
  return (
    <div className="rounded-md bg-white border border-slate-100 px-3 py-2 min-w-0" data-testid={testid}>
      <div className="text-lg font-semibold text-slate-900 leading-tight tracking-tightish truncate">{value}</div>
      <div className="text-[0.6rem] uppercase tracking-[0.12em] text-slate-500 mt-0.5 truncate">{label}</div>
    </div>
  );
}

// Every page load stops at each position (owner, 2026-10-12: "default should be pauses on feedback on page load");
// "Plays through" lasts for the visit only, never remembered.
type AngleSampleList = LiveSeries["kneeAtStrike"] extends infer K ? (K extends { perStrike: infer P } ? P : never) : never;


/** The timeline's readout: m:ss for the coaching video, tenths of a second for a swing a few seconds long. */
export function clockText(t: number, d: number): string {
  if (!(d > 0) || !Number.isFinite(d)) return "";
  if (d < 10) return `${t.toFixed(1)} / ${d.toFixed(1)}s`;
  const mmss = (x: number) => {
    const sec = Math.max(0, Math.floor(x));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  };
  return `${mmss(t)} / ${mmss(d)}`;
}

export default function LiveFreeAnalysis({
  clipUrl,
  poseFile,
  moments,
  activity,
  stillBase,
  watermarkStamp,
  pageCharts = [],
  stageGeometry = null,
  coaching = null,
  splitWindow = null,
  orderedFrameIds,
  liveSeries = null,
  cueStills = null,
  dwellMs = 2600,
  voice = null,
  voicePending = false,
  framesPending = false,
  stillVersion = null,
  suspended = false,
  requestCue = null,
  onActiveCueChange,
  requestPosition = null,
  onPositionChange,
  coachVideoUrl = null,
  coachOffer = null,
}: LiveFreeAnalysisProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLVideoElement | null>(null);
  const lastTimeRef = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  /** The timeline slider (owner, 2026-10-12): 0..1000 across the analysed span, driven from the draw loop. */
  const scrubRef = useRef<HTMLInputElement | null>(null);
  const SCRUB_STEPS = 1000;
  /** The thumb is being dragged: the draw loop leaves the slider alone until the pointer is up. (Focus is not the
   * test — a scrubbed slider keeps focus, and the slider then froze while the clip played on.) */
  const scrubbingRef = useRef(false);
  const consumedRef = useRef<Set<string>>(new Set());
  const dwellTimerRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  /** The clip has a frame to show; until then the stage shows a loader rather than black. */
  const [clipReady, setClipReady] = useState(false);
  // The first position's frame (P1), shown greyed behind the loader while the clip arrives.
  const loadingStillSrc = (() => {
    const first = moments.find((m) => !!m.stillUrl);
    if (!first?.stillUrl) return null;
    const u = `${stillBase}${first.stillUrl}`;
    return stillVersion ? `${u}${u.includes("?") ? "&" : "?"}v=${encodeURIComponent(stillVersion)}` : u;
  })();
  /** The held position was entered from a PLAYING clip (the automatic stop, or a chip tapped mid-play): the control
   * keeps showing Pause, Pause holds the still, and the clip plays on after the last cue (owner, 2026-10-12). */
  const [, setResumeAfterDwell] = useState(false);
  const resumeAfterDwellRef = useRef(false);
  const setResume = (v: boolean) => { resumeAfterDwellRef.current = v; setResumeAfterDwell(v); };
  /** The walk through a position's cues, while it is running: what to do when the cue is over and how long it
   * still has, so Pause can freeze it (voice paused, cue held) and Play continue it (owner, 2026-10-12: "pausing
   * playback during a pposition feedback slot should allow pause + continue within that pposition feedback"). */
  const walkRef = useRef<{ moveOn: () => void; holdMs: number; startedAt: number; voiced: boolean } | null>(null);
  const [walkState, setWalkState] = useState<"off" | "running" | "paused">("off");
  const walkStateRef = useRef<"off" | "running" | "paused">("off");
  const setWalk = (v: "off" | "running" | "paused") => { walkStateRef.current = v; setWalkState(v); };
  const [dwelling, setDwelling] = useState<DwellMoment | null>(null);
  const [autoPause, setAutoPause] = useState<boolean>(true);
  const [panelTick, setPanelTick] = useState(0); // panel refresh at the pose file rate while playing
  const [seriesPick, setSeriesPick] = useState<string | null>(null); // the big chart's series
  const [pickedTab, setPickedTab] = useState<"coaching" | "metrics" | null>(null);
  // The ONE selected cue (a row key). Never a position: selecting by position is what lit every row of
  // P1 at once and showed them all as one picture (owner, 2026-09-25).
  const [activeCue, setActiveCue] = useState<string | null>(null);
  // The coaching voice: one hidden player, a cue's clip at a time. A browser refuses sound before the viewer has
  // touched the page; then the walk runs on silently and the stage offers "Tap for sound".
  const voiceRef = useRef<HTMLAudioElement | null>(null);
  // The voice player is sounding a sentence right now: the row on the stage shows it speaking (owner, 2026-10-21).
  const [speaking, setSpeaking] = useState(false);
  const [voiceBlocked, setVoiceBlocked] = useState(false);
  // Muted: the voice still runs (so cues keep their timing), it is just silent.
  const [muted, setMuted] = useState<boolean>(loadMuted);
  useEffect(() => {
    if (voiceRef.current) voiceRef.current.muted = muted;
  }, [muted]);
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try { window.localStorage.setItem(MUTE_KEY, next ? "on" : "off"); } catch { /* preference only */ }
  };
  // Coaching: the narrated video (premium) or the upgrade page (free), first in the position row. While it is on, the
  // live clip, its stops and its voice are held, as when suspended.
  const coachRef = useRef<HTMLVideoElement | null>(null);
  const hasCoach = !!coachVideoUrl || !!coachOffer;
  const [coachOn, setCoachOn] = useState<boolean>(hasCoach);
  const coachOnRef = useRef(coachOn);
  coachOnRef.current = coachOn;
  const [coachPlaying, setCoachPlaying] = useState(false);
  /** The coaching timeline, written straight to the DOM every frame (as the swing's is) — a React state fed by
   * timeupdate moved it in 4 Hz jumps and pulled a dragged thumb back to the playing video. */
  const coachFillRef = useRef<HTMLDivElement | null>(null);
  const coachScrubRef = useRef<HTMLInputElement | null>(null);
  const coachScrubbingRef = useRef(false);
  /** "0:41 / 1:20" in Coaching, "0.8 / 1.6s" on the swing. */
  const timeRef = useRef<HTMLSpanElement | null>(null);
  // The browser refused the coaching video's sound before a tap: it plays silently and the stage offers the tap.
  const [coachSilent, setCoachSilent] = useState(false);
  // The coaching video is meant to be playing but has no picture yet (first load, or a stall): the stage shows P1's
  // frame and a loader instead of black (owner, 2026-11-01, 13797: "a blank screen... I can't just click play").
  const [coachLoading, setCoachLoading] = useState<boolean>(!!coachVideoUrl);
  // The upgrade page moves on to the swing by itself only when the player opens on it, not when Coaching is picked.
  const offerFirstRef = useRef(true);
  const held = suspended || coachOn;
  // The upgrade page on a short stage (a phone: about 228 px) drops its description and list, so its headline and its
  // button stay on screen; the full card is about 390 px tall.
  const [offerEl, setOfferEl] = useState<HTMLDivElement | null>(null);
  const [offerCompact, setOfferCompact] = useState(false);
  useEffect(() => {
    if (!offerEl || typeof ResizeObserver === "undefined") return;
    const measure = () => { if (offerEl.clientHeight > 0) setOfferCompact(offerEl.clientHeight < OFFER_FULL_MIN_PX); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(offerEl);
    return () => ro.disconnect();
  }, [offerEl]);
  useEffect(() => {
    if (coachRef.current) coachRef.current.muted = muted || coachSilent;
  }, [muted, coachSilent]);
  const [captionsOn, setCaptionsOn] = useState<boolean>(loadCaptions);
  const toggleCaptions = () => {
    const next = !captionsOn;
    setCaptionsOn(next);
    try { window.localStorage.setItem(CAPTIONS_KEY, next ? "on" : "off"); } catch { /* preference only */ }
  };
  const [panelOpen, setPanelOpen] = useState<boolean>(loadPanelOpen);
  const togglePanel = () => {
    const next = !panelOpen;
    setPanelOpen(next);
    try { window.localStorage.setItem(PANEL_KEY, next ? "open" : "closed"); } catch { /* preference only */ }
  };
  const [followOn, setFollowOn] = useState<boolean>(loadFollow);
  const toggleFollow = () => {
    const next = !followOn;
    setFollowOn(next);
    try { window.localStorage.setItem(FOLLOW_KEY, next ? "on" : "off"); } catch { /* preference only */ }
  };
  const sideRef = useRef<HTMLDivElement | null>(null);
  // Full screen (owner, 2026-10-12): the browser's own where it has one; iPhone Safari has none for anything but a
  // bare <video>, so there the player fills the window instead.
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      const isOn = !!playerRef.current && (doc.fullscreenElement ?? doc.webkitFullscreenElement) === playerRef.current;
      setNativeFullscreen(isOn);
      setFullscreen(isOn);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);
  useEffect(() => {
    if (!fullscreen || nativeFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFullscreen(false); };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [fullscreen, nativeFullscreen]);
  const toggleFullscreen = () => {
    const el = playerRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    const doc = document as Document & { webkitExitFullscreen?: () => void };
    if (fullscreen) {
      if (!nativeFullscreen) setFullscreen(false);
      else if (doc.exitFullscreen) doc.exitFullscreen().catch(() => undefined);
      else doc.webkitExitFullscreen?.();
      return;
    }
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => setFullscreen(true));
    } else if (el?.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else {
      setFullscreen(true);
    }
  };
  /** The `ended` listener of the clip being walked, so a stop or a hand-pick can drop it. */
  const voiceEndedRef = useRef<(() => void) | null>(null);
  const voicingRef = useRef<PremiumFeedbackItem | null>(null);
  const coachingRows = useMemo(
    () => (coaching?.rows ?? []).map((r) => ({ ...r, key: r.key ?? `${r.pLabel}:${r.overlay}` })),
    [coaching?.rows]
  );
  const cueKeysAt = (label: string) => coachingRows.filter((r) => r.pLabel === label).map((r) => r.key);
  const cueKeysAtRef = useRef(cueKeysAt);
  cueKeysAtRef.current = cueKeysAt;
  const activeCueRef = useRef(activeCue);
  activeCueRef.current = activeCue;
  // What the stage shows while held: the selected cue's OWN still; if this upload has none for it (rendered
  // before cue stills existed), the position's composite — labelled, so it is never mistaken for one cue.
  const activeRowData = activeCue ? coachingRows.find((r) => r.key === activeCue) : undefined;
  const activeCueStill =
    dwelling && activeRowData && activeRowData.pLabel === dwelling.label
      ? cueStillFor(cueStills, activeRowData.pLabel, activeRowData.overlay)
      : null;
  const stageStillUrl = activeCueStill ?? dwelling?.stillUrl ?? null;
  // The server's frame for the stage: a loader until it has loaded.
  const stageStillSrc = stageStillUrl
    ? `${stillBase}${stageStillUrl}${stillVersion ? `${stageStillUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(stillVersion)}` : ""}`
    : null;
  const [loadedStill, setLoadedStill] = useState<string | null>(null);
  // Cue to cue the frames CROSSFADE (owner, 2026-10-12: "the transition between overlays is a bit ugly ... softer"):
  // the previous frame stays underneath until the next has faded in over it, so only the overlays seem to change —
  // no dip to the paused clip and its skeleton in between.
  const [underStill, setUnderStill] = useState<string | null>(null);
  const shownStillRef = useRef<string | null>(null);
  const CROSSFADE_MS = 320;
  // Smooth transitions between the clip and the frames (owner, 2026-10-12: no black flicker — "invisible or smooth"):
  // the still layer is transparent, so the paused clip stays in view until the frame has loaded, then the frame
  // fades in over it; when the position is let go of the frame fades out over the playing clip. Every frame is
  // fetched ahead of time, so a cue-to-cue swap is instant and the first stop is at most one short fade.
  const [leavingStill, setLeavingStill] = useState<{ src: string; alt: string } | null>(null);
  const lastStillRef = useRef<{ src: string; alt: string } | null>(null);
  const STILL_FADE_MS = 220;
  useEffect(() => {
    if (dwelling) return;
    const last = lastStillRef.current;
    if (!last) return;
    lastStillRef.current = null;
    setLeavingStill(last);
    const t = window.setTimeout(() => setLeavingStill(null), STILL_FADE_MS);
    return () => window.clearTimeout(t);
  }, [dwelling]);
  useEffect(() => {
    const urls = new Set<string>();
    for (const m of moments) if (m.stillUrl) urls.add(`${stillBase}${m.stillUrl}`);
    for (const c of cueStills ?? []) if (c.stillUrl) urls.add(`${stillBase}${c.stillUrl}`);
    urls.forEach((u) => {
      const src = stillVersion ? `${u}${u.includes("?") ? "&" : "?"}v=${encodeURIComponent(stillVersion)}` : u;
      const img = new Image();
      img.src = src;
    });
  }, [moments, cueStills, stillBase, stillVersion]);
  const showingAllCues = !!dwelling && !activeCueStill && !!activeRowData;
  useEffect(() => {
    const prev = shownStillRef.current;
    shownStillRef.current = dwelling ? stageStillSrc : null;
    // A new frame within the same held position: the shown one stays underneath while the new one fades in.
    if (dwelling && prev && stageStillSrc && prev !== stageStillSrc) setUnderStill(prev);
    if (!dwelling) setUnderStill(null);
  }, [stageStillSrc, dwelling]);
  useEffect(() => {
    if (!underStill || loadedStill !== stageStillSrc) return;
    const t = window.setTimeout(() => setUnderStill(null), CROSSFADE_MS);
    return () => window.clearTimeout(t);
  }, [underStill, loadedStill, stageStillSrc]);
  // The coaching sentence over the top of the frame, as the analysis video shows it (owner, 2026-10-12): the cue's
  // spoken sentence when there is a voice, else the cue's own description.
  const caption = dwelling && activeRowData
    ? (voice?.items.find((it) => it.position === activeRowData.pLabel && it.overlay === activeRowData.overlay)?.text
        ?? activeRowData.description ?? "").trim()
    : "";
  // Coaching opens first when there is any; rows can arrive after mount, so the default is
  // derived until the user picks a tab.
  const tab: "coaching" | "metrics" = pickedTab ?? (coachingRows.length > 0 ? "coaching" : "metrics");
  // The draw loop reads the current tab without re-subscribing: the 10 Hz panel tick (a
  // component-wide React re-render) runs only while the Metrics tab consumes it, so it never
  // competes with the canvas on the main thread while the coaching list is up.
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const fps = captureFpsFor(splitWindow, poseFile);
  // The artifact is only shown when it was computed at THIS cadence: at another one its numbers
  // are wrong (cadence, timings), so a stale one is hidden behind a note, never displayed.
  const series = liveSeriesOnCadence(liveSeries, fps) ? liveSeries : null;
  // The strikes as split-frame indices for the draw loop (a ref: the loop never re-subscribes).
  const strikeIdxRef = useRef<{ left: number[]; right: number[] }>({ left: [], right: [] });
  strikeIdxRef.current = series
    ? { right: series.strikes.map((f) => f - series.minFrame), left: (series.leftStrikes ?? []).map((f) => f - series.minFrame) }
    : { left: [], right: [] };
  const aligned = useMemo(
    () => (orderedFrameIds && orderedFrameIds.length > 0 ? alignPoseFileToFrameList(poseFile, orderedFrameIds) : poseFile),
    [poseFile, orderedFrameIds]
  );
  const frames = useMemo(() => aligned.frames.map(framePoints), [aligned]);
  // The pose file's time base: frame i ↔ spanStart + i/fps; the analysed span ends at the last
  // frame that HAS a pose (or the window's end), whichever is first — never past the clip.
  const spanStart = Math.max(0, splitWindow?.startSec ?? 0);
  const posedFrames = useMemo(() => {
    let last = -1;
    frames.forEach((f, i) => {
      if (f.size > 0) last = i;
    });
    return last + 1;
  }, [frames]);
  const spanEndFor = (duration: number) =>
    Math.min(splitWindow?.stopSec ?? Infinity, spanStart + posedFrames / fps, duration > 0 ? duration : Infinity);
  const relTime = (t: number) => Math.max(0, t - spanStart);
  const isGait = activity === "runGait";
  // The swing's positions in order, each with its moment when the server gave one. Golf has the ten P-positions
  // (P5 and P8 have no feedback yet: grey boxes); other techniques list what the server returned.
  const positionSlots = useMemo(() => {
    const byLabel = new Map(moments.map((m) => [m.label, m] as const));
    const order = activity.startsWith("golfSwing") ? GOLF_POSITIONS : moments.map((m) => m.label);
    const slots = order.map((label) => ({ label, moment: byLabel.get(label) ?? null }));
    for (const m of moments) if (!order.includes(m.label)) slots.push({ label: m.label, moment: m });
    return slots;
  }, [activity, moments]);

  // ONE geometry across free and premium (user decision, 2026-09-12): the clip plays exactly
  // where the renderer placed it, cropped exactly as the renderer cropped it, on the canvas the
  // stills were composed on — the PUBLISHED stage when this upload has one (the body-framing
  // crop that makes the runner fill the frame), else the sport's known canvas and the ported
  // placement math, uncropped (../live/liveAnalysis, tested).
  const geometry = useMemo(
    () => playbackGeometry(activity, poseFile.captureWidth || 720, poseFile.captureHeight || 1280, stageGeometry),
    [poseFile, activity, stageGeometry]
  );
  const stage = geometry.canvas;
  // No athlete zoom (owner, 2026-10-12: "remove the athlete zoom functionality for now it just doesnt work"):
  // the clip plays exactly where the renderer placed it and the held still is the whole composition, so a
  // stop never resizes and nothing is cut. The crop helpers stay in ../live/liveAnalysis, tested, unused.
  const windowStyle = placementStyle(geometry.placement, stage);
  const clipStyle = cropStyle(geometry.crop);
  const stillStyle = { left: "0%", top: "0%", width: "100%", height: "100%" };

  // The stage's on-screen size, measured and set in PIXELS. CSS aspect-ratio with w-full +
  // max-h-full silently breaks the ratio in a wide container (the browser keeps the forced
  // width and clamps the height) — which is exactly how the still and the clip ended up in
  // different boxes on 17816. Contain-fit the stage into the panel ourselves; no negotiation.
  const [stageBox, setStageBox] = useState<{ w: number; h: number } | null>(null);
  // The stage ends where the control bar starts. The bar is a row tall when wide and two on a phone (the pills on a
  // row of their own), so it is measured: a fixed 44 px left a 52 px bar over the stage's foot (375 px, 2026-11-01).
  const barRef = useRef<HTMLDivElement | null>(null);
  const [barHeight, setBarHeight] = useState(52);
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => { if (el.offsetHeight > 0) setBarHeight(el.offsetHeight); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fit = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw <= 0 || ch <= 0) return;
      const k = Math.min(cw / stage.w, ch / stage.h);
      setStageBox({ w: Math.round(stage.w * k), h: Math.round(stage.h * k) });
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stage]);


  // The draw loop. State is NOT touched per frame (that re-rendered the whole component at
  // display rate — the other half of the jitter): the progress bar is written straight to the
  // DOM, and the panel refreshes at the pose file rate through a value that changes 10× a second.
  useEffect(() => {
    let raf = 0;
    // The clock (user, 2026-09-19: "the pose looks a bit behind the motion"). The split frames
    // pair exactly with the clip (frame 20 = 2.00 s, measured), so the lag was currentTime, which
    // trails the frame actually on screen. Where the browser offers it, the presented frame's own
    // mediaTime (requestVideoFrameCallback) is the time the pose is drawn at, advanced by the time
    // since that frame was painted; elsewhere, and while paused, currentTime as before.
    const video0 = videoRef.current;
    let presented: { mediaTime: number; at: number } | null = null;
    let vfc = 0;
    const withVfc = video0 as (HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number; cancelVideoFrameCallback?: (h: number) => void }) | null;
    const onPresented = (now: number, meta: { mediaTime: number }) => {
      presented = { mediaTime: meta.mediaTime, at: now };
      if (withVfc?.requestVideoFrameCallback) vfc = withVfc.requestVideoFrameCallback(onPresented);
    };
    if (withVfc?.requestVideoFrameCallback) vfc = withVfc.requestVideoFrameCallback(onPresented);
    // A seek (the loop wrap, a moment click, the dwell engine) makes the cached presented frame
    // stale until the browser paints the frame at the seek target — and nothing is presented
    // while the seek is in flight. Reading the stale time at the span end re-issued the wrap seek
    // every tick, each cancelling the last, and the clip stuck at its end (user, 2026-09-19).
    // From the seek until the next presented frame the clock is currentTime, the seek target.
    const onSeeking = () => {
      presented = null;
    };
    video0?.addEventListener("seeking", onSeeking);
    const clock = (video: HTMLVideoElement) => {
      if (!presented || video.paused || video.seeking) return video.currentTime;
      const since = Math.max(0, Math.min(0.05, (performance.now() - presented.at) / 1000));
      return presented.mediaTime + since;
    };
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video) return;
      const t = clock(video);
      // Loop inside the analysed span: past its end (or before its start) jump back to the start.
      const spanEnd = spanEndFor(video.duration);
      if (Number.isFinite(spanEnd) && (t >= spanEnd - 0.02 || t < spanStart - 0.05)) {
        video.currentTime = spanStart;
        return;
      }
      // The panel (numbers, chart cursors) follows playback at the pose file's own rate — 10 Hz —
      // independent of whether the canvas can draw; the progress bar is a direct DOM write.
      if (Number.isFinite(spanEnd) && spanEnd > spanStart) {
        const frac = relTime(t) / (spanEnd - spanStart);
        if (progressRef.current) progressRef.current.style.width = `${frac * 100}%`;
        if (scrubRef.current && !scrubbingRef.current)
          scrubRef.current.value = String(Math.round(frac * SCRUB_STEPS));
        if (timeRef.current && !coachOnRef.current)
          timeRef.current.textContent = clockText(Math.min(Math.max(relTime(t), 0), spanEnd - spanStart), spanEnd - spanStart);
      }
      if (tabRef.current === "metrics") setPanelTick(Math.floor(relTime(t) * PANEL_TICK_HZ));
      const ctx = canvas.getContext ? canvas.getContext("2d") : null;
      if (!ctx) return;
      // Loop wrap (the clip loops): every moment re-arms, and the backdrop follows.
      if (t < lastTimeRef.current - 0.5) consumedRef.current.clear();
      lastTimeRef.current = t;
      const bg = backdropRef.current;
      if (bg && Math.abs(bg.currentTime - t) > 0.2) bg.currentTime = t;
      const trailSeconds = strideSeconds(series) ?? TRAIL_SECONDS;
      const floatIdx = Math.min(frames.length - 1, Math.max(0, relTime(t) * fps));
      const idx = Math.floor(floatIdx);
      // The pose NOW: interpolated between the two pose frames around this instant. The trail,
      // its anchor and the skeleton all read this one sample, so nothing sits a whole pose frame
      // behind the clip (user, 2026-09-19: "the pose looks a bit behind the motion" — the trail's
      // head and anchor were the last whole frame, up to 100 ms behind the interpolated ankle).
      const pts = interpolatedFramePoints(frames, floatIdx);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (isGait) {
        // The ankle trail is ONE tapered ribbon per foot, spanning ONE STRIDE.
        //
        // Both halves matter (user, 2026-09-19, on this upload: "17820 is a very strong cadence of
        // running", then "if its more consistent then it can be stronger"). At 204 spm the fixed
        // four-second window laid 6.7 strides of ankle path over each other, so the picture never
        // held a shape and the glow had to stay faint to avoid filling the frame. One stride draws
        // one loop, and a stride repeats, so the loop is the same every step. And the path is
        // filled once rather than stroked per frame: the per-segment halos used to stack into the
        // patchy look, where a single path takes a single even halo (and costs 0.04 ms a frame
        // against 1.93 ms, measured in Chromium).
        const trailFrames = Math.round(trailSeconds * fps);
        const first = Math.max(0, idx - trailFrames);
        // Anchored to the body (user, 2026-09-19): every past ankle point is drawn relative to where
        // the head is NOW, so a camera pan or shake moves the whole trail with the runner instead
        // of smearing it. The head (nose), else the hip midpoint; a frame without either is drawn
        // as-is.
        const anchorOf = (f: Map<string, { x: number; y: number }> | undefined) => {
          if (!f) return null;
          const nose = f.get("nose");
          if (nose) return nose;
          const lh = f.get("left_hip");
          const rh = f.get("right_hip");
          return lh && rh ? { x: (lh.x + rh.x) / 2, y: (lh.y + rh.y) / 2 } : null;
        };
        const anchorNow = anchorOf(pts);
        const relTo = (p: { x: number; y: number }, i: number) => {
          const a = anchorNow ? anchorOf(frames[i]) : null;
          return a && anchorNow ? { x: p.x - a.x + anchorNow.x, y: p.y - a.y + anchorNow.y } : p;
        };
        ctx.lineCap = "round";
        for (const [color, name] of [
          [TRAIL_LEFT, "left_ankle"],
          [TRAIL_RIGHT, "right_ankle"],
        ] as const) {
          // The whole frames up to this one, then the interpolated point as the head (already in
          // the current anchor's frame, so it is not re-anchored).
          const path: Array<{ x: number; y: number }> = [];
          for (let i = first; i <= idx; i++) {
            const raw = frames[i]?.get(name);
            if (raw) path.push(relTo(raw, i));
          }
          const head = pts.get(name);
          if (head && floatIdx > idx) path.push(head);
          const outline = ribbonOutline(path, TRAIL_HEAD_WIDTH * LOOK_STRENGTH, TRAIL_TAPER);
          if (outline.length === 0) continue;
          const tail = path[0];
          const tip = path[path.length - 1];
          const grad = ctx.createLinearGradient ? ctx.createLinearGradient(tail.x, tail.y, tip.x, tip.y) : null;
          if (grad) {
            grad.addColorStop(0, color + "00");
            grad.addColorStop(0.5, color + "77");
            grad.addColorStop(1, color);
          }
          ctx.shadowColor = color;
          ctx.shadowBlur = TRAIL_GLOW * LOOK_STRENGTH;
          ctx.fillStyle = grad ?? color;
          ctx.beginPath();
          ctx.moveTo(outline[0].x, outline[0].y);
          for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i].x, outline[i].y);
          ctx.closePath();
          ctx.fill();
          ctx.shadowBlur = 0;
        }
        // Foot strike: a ring in the foot's colour pulses out from where the ankle LANDED (the
        // strike frame's point, not the moving cursor) and fades within PULSE_SECONDS. Both
        // feet: the pass detects left and right strikes with the same rule.
        for (const [color, name, strikeIdx] of [
          [TRAIL_LEFT, "left_ankle", strikeIdxRef.current.left],
          [TRAIL_RIGHT, "right_ankle", strikeIdxRef.current.right],
        ] as const) {
          for (const { index, progress } of activeStrikePulses(strikeIdx, floatIdx, fps)) {
            // The pulse marks the CONTACT, so it is planted at the landing point for the whole
            // pulse — the lead-in included (user, 2026-09-20: "it shouldnt trace the foot, it
            // should just stay where it occurs"). It used to travel with the ankle until the
            // strike frame, which read as the mark chasing the foot rather than marking the spot.
            const at = frames[index]?.get(name);
            if (!at) continue;
            const ring = pulseRing(progress);
            ctx.strokeStyle = color;
            ctx.shadowColor = color;
            ctx.shadowBlur = 6;
            ctx.globalAlpha = ring.alpha;
            ctx.lineWidth = ring.width;
            ctx.beginPath();
            ctx.arc(at.x, at.y, ring.radius, 0, Math.PI * 2);
            ctx.stroke();
            // The centre: a solid dot in the foot's colour (user, 2026-09-19: the leg colours, not
            // white, and a larger pulse). It was briefly given a radial-gradient core and scaled up
            // with the ribbon; the owner preferred it as it was (2026-09-20: "i prefer the older
            // pulses"), so the strength dial moves the TRAIL only.
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(at.x, at.y, PULSE_CORE_RADIUS, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      ctx.shadowBlur = 0;
      // The pose is coloured per side like the reference clip (user, 2026-09-19): left limbs in
      // the left colour, right limbs in the right one, the torso's cross-body bones neutral.
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      for (const [a, b] of SKELETON_BONES) {
        const pa = pts.get(a);
        const pb = pts.get(b);
        if (!pa || !pb) continue;
        ctx.strokeStyle = sideColor(a, b);
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }
      pts.forEach((p, name) => {
        ctx.fillStyle = name.startsWith("left_") ? TRAIL_LEFT : name.startsWith("right_") ? TRAIL_RIGHT : JOINT;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2);
        ctx.fill();
      });
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      video0?.removeEventListener("seeking", onSeeking);
      if (vfc && withVfc?.cancelVideoFrameCallback) withVfc.cancelVideoFrameCallback(vfc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frames, fps, isGait, spanStart, splitWindow?.stopSec]);

  // Walk a position's cues one at a time, dwellMs each — the Pro video's rhythm — then play on. With no
  // cue rows the position's composite holds for one dwell, as before.
  const voiceFor = (key: string | null): PremiumFeedbackItem | undefined => {
    const row = key ? coachingRows.find((r) => r.key === key) : undefined;
    return row ? voice?.items.find((it) => it.position === row.pLabel && it.overlay === row.overlay && !!it.audio) : undefined;
  };
  // Every cue step, and every stop, takes a new step number: a callback from a superseded step (a sound refusal that
  // arrives after another position is on show) sees a different number and does nothing (owner, 2026-10-13).
  const walkStepRef = useRef(0);
  // A cue whose sentence is still being voiced (free: Kokoro in the browser, owner 2026-10-13) holds the walk here until
  // its clip lands; any stop lets go of it.
  const waitingVoiceRef = useRef<{ keys: string[]; i: number } | null>(null);
  const [waitingVoice, setWaitingVoice] = useState(false);
  /** The cue has a sentence whose clip is not made yet. */
  const voiceComingFor = (key: string | null): boolean => {
    const row = key ? coachingRows.find((r) => r.key === key) : undefined;
    return !!row && !!voice?.items.some((it) => it.position === row.pLabel && it.overlay === row.overlay && !it.audio);
  };
  const stopVoice = () => {
    walkStepRef.current++;
    waitingVoiceRef.current = null;
    setWaitingVoice(false);
    voicingRef.current = null;
    walkRef.current = null;
    setWalk("off");
    const a = voiceRef.current;
    if (a && voiceEndedRef.current) a.removeEventListener("ended", voiceEndedRef.current);
    voiceEndedRef.current = null;
    try { a?.pause(); } catch { /* nothing playing */ }
  };
  /** Plays the cue's clip; `onDone` fires once when it has ENDED, or at once when it cannot play. */
  const playVoice = (item: PremiumFeedbackItem | undefined, onDone?: () => void, onBlocked?: () => void) => {
    stopVoice();
    const a = voiceRef.current;
    if (!item || !a || !voice) { onDone?.(); return; }
    voicingRef.current = item;
    a.src = `${voice.clipBase}${item.audio}`;
    if (onDone) {
      const done = () => { a.removeEventListener("ended", done); onDone(); };
      a.addEventListener("ended", done);
      voiceEndedRef.current = done;
    }
    const started = a.play?.();
    if (started && typeof started.catch === "function") {
      // Refused (no tap on the page yet): the cue holds for its sentence's length silently, Tap for sound offered —
      // it used to move on at once, so a first stop after page load flashed through every cue.
      started.catch((err: unknown) => {
        // Not a refusal: this clip was interrupted by the next one (or a pause), which plays. Treating it as blocked
        // offered "Tap to hear your coaching" over coaching that was audibly playing (owner, 2026-11-01, 13797 P7).
        if ((err as { name?: string } | null)?.name === "AbortError" || voicingRef.current !== item) return;
        setVoiceBlocked(true);
        if (voiceEndedRef.current) a.removeEventListener("ended", voiceEndedRef.current);
        voiceEndedRef.current = null;
        onBlocked?.();
      });
    }
  };
  // A stalled clip (no `ended`): give up after its length plus 3 s so the walk can never hang.
  const VOICE_GRACE_MS = 3000;
  const holdMsFor = (item: PremiumFeedbackItem | undefined) =>
    item ? Math.round((item.seconds || 0) * 1000) + VOICE_GRACE_MS : dwellMs;

  // Each cue holds until its sentence has been said (the clip's `ended`), or dwellMs with no voice. After the
  // last cue the clip plays on (an automatic stop) or the last cue stays up (a hand-picked position: it never
  // resumes on its own, 2026-09-12).
  const walkCues = (keys: string[], i: number) => {
    const key = keys[i] ?? null;
    setActiveCue(key);
    const clip = voiceFor(key);
    if (!clip && voicePending && voiceComingFor(key)) {
      stopVoice();
      waitingVoiceRef.current = { keys, i };
      setWaitingVoice(true);
      return;
    }
    let moved = false;
    let step = -1;
    const moveOn = () => {
      if (moved || step !== walkStepRef.current) return;
      moved = true;
      if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
      voiceEndedRef.current = null;
      walkRef.current = null;
      if (i + 1 < keys.length) {
        // The latest render's walk: this closure's view of the voice is from when the cue started, and a clip made
        // since (free: voiced in the browser) would be missed and waited for forever.
        walkCuesRef.current(keys, i + 1);
        return;
      }
      dwellTimerRef.current = null;
      setWalk("off");
      if (!resumeAfterDwellRef.current) return; // a position picked while paused holds its last cue until Play
      stopVoice();
      setDwelling(null);
      setActiveCue(null);
      playOn();
    };
    const silentHold = () => {
      if (moved || !clip || step !== walkStepRef.current) return;
      if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
      const ms = Math.max(dwellMs, Math.round((clip.seconds || 0) * 1000) + 400);
      walkRef.current = { moveOn, holdMs: ms, startedAt: Date.now(), voiced: false };
      dwellTimerRef.current = window.setTimeout(moveOn, ms);
    };
    // playVoice stops the previous clip first, which also clears any walk: start this one after it.
    playVoice(clip, clip ? moveOn : undefined, silentHold);
    step = walkStepRef.current; // playVoice stopped the step before this one
    const holdMs = holdMsFor(clip);
    walkRef.current = { moveOn, holdMs, startedAt: Date.now(), voiced: !!clip };
    setWalk("running");
    dwellTimerRef.current = window.setTimeout(moveOn, holdMs);
  };

  /** The clip plays on from a held position; the position is spent, so the stop engine does not take it again. */
  const playOn = () => {
    if (dwelling) consumedRef.current.add(dwelling.label);
    videoRef.current?.play().catch(() => undefined);
  };

  // Pause freezes the walk where it is: the voice pauses mid-sentence, the cue stays up, the timer keeps what is
  // left. Play picks it up from there.
  const pauseWalk = () => {
    const w = walkRef.current;
    if (!w) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    w.holdMs = Math.max(0, w.holdMs - (Date.now() - w.startedAt));
    try { voiceRef.current?.pause(); } catch { /* nothing playing */ }
    setWalk("paused");
  };
  const continueWalk = () => {
    const w = walkRef.current;
    if (!w) return;
    w.startedAt = Date.now();
    if (w.voiced) {
      const started = voiceRef.current?.play?.();
      if (started && typeof started.catch === "function") started.catch(() => setVoiceBlocked(true));
    }
    dwellTimerRef.current = window.setTimeout(w.moveOn, w.holdMs);
    setWalk("running");
  };
  const walkCuesRef = useRef(walkCues);
  walkCuesRef.current = walkCues;
  // The clip the held cue was waiting for has landed (or voicing has finished without it): walk on from that cue.
  useEffect(() => {
    const w = waitingVoiceRef.current;
    if (!w) return;
    if (!voicePending || voiceFor(w.keys[w.i] ?? null)) {
      waitingVoiceRef.current = null;
      setWaitingVoice(false);
      walkCuesRef.current(w.keys, w.i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice, voicePending]);

  // The dwell timer belongs to the component, not to the effect below: that effect re-runs whenever
  // `dwelling` changes — which starting a dwell does — and its cleanup used to clear this timer, so a
  // dwell never resumed on its own.
  useEffect(() => () => {
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
  }, []);

  // The dwell engine: crossing an unconsumed moment pauses the clip and walks its cues' stills;
  // after the last one the clip resumes on its own. "Play through" (the persisted preference)
  // skips the pause entirely.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || moments.length === 0 || !autoPause) return;
    const onTime = () => {
      if (dwelling) return;
      const t = video.currentTime;
      const next = moments.find(
        (m) => !consumedRef.current.has(m.label) && t >= m.timeSec && t < m.timeSec + 1.0
      );
      // A position whose frame is still being rendered stops too, with a loader; with no frame coming, it plays on.
      if (!next || (!next.stillUrl && !framesPending)) return;
      consumedRef.current.add(next.label);
      video.pause();
      setResume(true);
      setDwelling(next);
      walkCuesRef.current(cueKeysAtRef.current(next.label), 0);
    };
    video.addEventListener("timeupdate", onTime);
    const onSeeked = () => {
      // Re-arm every moment at or after the new position so a replayed section dwells again.
      const t = video.currentTime;
      for (const m of moments) if (m.timeSec >= t - 0.05) consumedRef.current.delete(m.label);
    };
    video.addEventListener("seeked", onSeeked);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("seeked", onSeeked);
    };
  }, [moments, dwelling, dwellMs, autoPause, framesPending]);

  const toggleAutoPause = () => {
    const next = !autoPause;
    setAutoPause(next);
    // A position on show finishes its feedback first; the setting applies from the next moment on (owner,
    // 2026-10-12: "it should only take effect after the pposition audio has completed playback").
  };

  // Suspended, or Coaching on: stop everything where it is and hold it.
  useEffect(() => {
    if (!held) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    stopVoice();
    setDwelling(null);
    setActiveCue(null);
    try { videoRef.current?.pause(); } catch { /* jsdom */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held]);

  // Leaves Coaching for the live view; `playSwing` plays the clip on (the upgrade page's time is up, or Play).
  const leaveCoach = (playSwing = false) => {
    if (!coachOnRef.current) return;
    coachOnRef.current = false;
    offerFirstRef.current = false;
    try { coachRef.current?.pause(); } catch { /* jsdom */ }
    setCoachOn(false);
    if (playSwing && !suspended) videoRef.current?.play().catch(() => undefined);
  };
  const enterCoach = () => {
    offerFirstRef.current = false;
    // Picking Coaching is a tap, so the browser allows sound now: try it with sound again (13797 on prod, 2026-11-01:
    // back from P3, whose voice was audible, the video stayed silent behind "Tap to hear your coaching").
    setCoachSilent(false);
    setCoachOn(true);
  };
  // Coaching on: the video plays (with sound if the browser allows, silently with a tap offered if not); the upgrade
  // page, when the player opened on it, gives way to the swing after COACH_OFFER_MS.
  useEffect(() => {
    if (!coachOn || suspended) return;
    if (coachVideoUrl) {
      const c = coachRef.current;
      if (!c) return;
      c.muted = muted || coachSilent;
      const started = c.play?.();
      if (started && typeof started.catch === "function") {
        started.catch((err: unknown) => {
          if ((err as { name?: string } | null)?.name === "AbortError" || !coachOnRef.current) return;
          setCoachSilent(true);
          c.muted = true;
          c.play?.()?.catch?.(() => undefined);
        });
      }
      return;
    }
    if (!offerFirstRef.current) return;
    const t = window.setTimeout(() => leaveCoach(true), COACH_OFFER_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachOn, coachVideoUrl, suspended]);
  // The coaching goes away (an upgrade bought, the video withdrawn): back to the live view.
  useEffect(() => {
    if (!hasCoach && coachOnRef.current) leaveCoach(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCoach]);

  // The Coaching list follows the cue on the stage: when its row is out of view, the list (not the page) glides it to a
  // third of the way down; a row already in view is left where it is.
  useEffect(() => {
    if (!followOn || !panelOpen || tab !== "coaching" || !activeCue) return;
    const panel = sideRef.current;
    const row = panel?.querySelector<HTMLElement>('[data-testid="live-coaching-row"][data-active="true"]');
    if (!panel || !row) return;
    const p = panel.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (r.top >= p.top && r.bottom <= p.bottom) return;
    panel.scrollTo?.({ top: panel.scrollTop + (r.top - p.top) - panel.clientHeight / 3, behavior: "smooth" });
  }, [activeCue, followOn, panelOpen, tab]);

  const togglePlay = () => {
    if (coachOnRef.current) {
      const c = coachRef.current;
      if (!coachVideoUrl || !c) { leaveCoach(true); return; } // the upgrade page: Play plays the swing
      if (c.paused) c.play()?.catch(() => undefined);
      else c.pause();
      return;
    }
    const video = videoRef.current;
    if (!video || suspended) return;
    if (dwelling) {
      if (walkStateRef.current === "running") { pauseWalk(); return; }
      if (walkStateRef.current === "paused") { continueWalk(); return; }
      // A cue picked by hand (a row, a coaching sentence): Play runs on from it — the position's remaining cues,
      // then the clip (owner, 2026-10-12: "if i click play then it should allow it to just run the frames").
      const keys = cueKeysAt(dwelling.label);
      const idx = activeCueRef.current ? keys.indexOf(activeCueRef.current) : -1;
      if (idx >= 0 && idx + 1 < keys.length) {
        setResume(true);
        walkCues(keys, idx + 1);
        return;
      }
      // Held after the walk: let go and play on.
      if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
      stopVoice();
      setDwelling(null);
      setActiveCue(null);
      playOn();
      return;
    }
    if (video.paused) video.play().catch(() => undefined);
    else video.pause();
  };

  // Click-enabled stills: show a position's still at its moment and hold it until play.
  const showStill = (m: DwellMoment) => {
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    video.currentTime = m.timeSec;
    video.pause();
    consumedRef.current.add(m.label);
    stopVoice();
    setDwelling(m);
  };

  // One cue, on its own: hold the clip at its position and show that cue's still. Manual — it stops any
  // walk in progress and holds until Play, like a still.
  const showCue = (key: string | null) => {
    leaveCoach();
    const row = key ? coachingRows.find((r) => r.key === key) : undefined;
    const m = row ? moments.find((x) => x.label === row.pLabel) : undefined;
    if (!row || !m) return;
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    video.currentTime = m.timeSec;
    video.pause();
    consumedRef.current.add(m.label);
    setResume(false);
    setDwelling(m);
    setActiveCue(row.key);
    // A cue picked by hand says its sentence (a tap: the browser allows sound now).
    setVoiceBlocked(false);
    playVoice(voiceFor(row.key));
  };

  // A position chip walks EVERY cue of that position, each with its voice, and holds on the last (owner,
  // 2026-10-12: "on p4 i only see the first overlay set, but there should be more"). No cue rows: the composite.
  const openPosition = (m: DwellMoment) => {
    leaveCoach();
    // The position already on show: let go of it and the swing goes on (owner, 2026-10-12: "clicking on a
    // selected p position should just continue the swing").
    if (dwelling && dwelling.label === m.label) {
      if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
      stopVoice();
      setDwelling(null);
      setActiveCue(null);
      playOn();
      return;
    }
    const keys = cueKeysAt(m.label);
    if (keys.length === 0) {
      if (m.stillUrl) showStill(m);
      else seekTo(m);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    // Tapped mid-play: the clip is still "playing" through the position (Pause offered, plays on after); tapped
    // while paused: the last cue holds until Play (owner, 2026-10-12).
    setResume(!video.paused || resumeAfterDwellRef.current && !!dwelling);
    video.currentTime = m.timeSec;
    video.pause();
    consumedRef.current.add(m.label);
    setDwelling(m);
    setVoiceBlocked(false); // a tap: the browser allows sound now
    walkCues(keys, 0);
  };

  // Coaching's timeline follows the narrated video every frame; a thumb being dragged is left where the finger is.
  const paintCoachTimeline = (t: number, d: number) => {
    if (!(d > 0) || !Number.isFinite(d)) return;
    const frac = Math.min(1, Math.max(0, t / d));
    if (coachFillRef.current) coachFillRef.current.style.width = `${frac * 100}%`;
    if (coachScrubRef.current) coachScrubRef.current.value = String(Math.round(frac * SCRUB_STEPS));
    if (timeRef.current) timeRef.current.textContent = clockText(t, d);
  };
  useEffect(() => {
    if (!coachOn || !coachVideoUrl) return;
    let raf = 0;
    const tick = () => {
      const c = coachRef.current;
      if (c && !coachScrubbingRef.current) paintCoachTimeline(c.currentTime, c.duration);
      raf = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachOn, coachVideoUrl]);
  const scrubCoach = (steps: number) => {
    const c = coachRef.current;
    if (!c || !(c.duration > 0) || !Number.isFinite(c.duration)) return;
    const t = (Math.min(SCRUB_STEPS, Math.max(0, steps)) / SCRUB_STEPS) * c.duration;
    c.currentTime = t;
    paintCoachTimeline(t, c.duration);
  };
  const endCoachScrub = () => { coachScrubbingRef.current = false; };

  // The slider: the clip goes where the thumb is, inside the analysed span; a held position is let go of
  // (its still clears, the voice stops) and the moments after the new time re-arm through `seeked`.
  const scrubTo = (steps: number) => {
    const video = videoRef.current;
    if (!video) return;
    const spanEnd = spanEndFor(video.duration);
    if (!Number.isFinite(spanEnd) || spanEnd <= spanStart) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    stopVoice();
    setDwelling(null);
    setActiveCue(null);
    const frac = Math.min(1, Math.max(0, steps / SCRUB_STEPS));
    video.currentTime = spanStart + frac * (spanEnd - spanStart);
    if (progressRef.current) progressRef.current.style.width = `${frac * 100}%`;
    if (timeRef.current) timeRef.current.textContent = clockText(frac * (spanEnd - spanStart), spanEnd - spanStart);
  };

  // One frame back or forward at the pose file's rate, paused, inside the analysed span; a held position is let go of.
  const stepFrame = (delta: 1 | -1) => {
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    stopVoice();
    setDwelling(null);
    setActiveCue(null);
    video.pause();
    const spanEnd = spanEndFor(video.duration);
    const hi = Number.isFinite(spanEnd) && spanEnd > spanStart ? spanEnd - 0.03 : Infinity;
    video.currentTime = Math.min(hi, Math.max(spanStart, video.currentTime + delta / fps));
  };

  const seekTo = (m: DwellMoment) => {
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    stopVoice();
    setDwelling(null);
    video.currentTime = Math.max(0, m.timeSec - 0.6);
    video.play().catch(() => undefined);
  };

  // Arrow keys step cue by cue, like the Next / Previous buttons — never while the user is typing.
  const showCueRef = useRef(showCue);
  showCueRef.current = showCue;
  // The coaching list below picked a sentence: show its cue here, with its voice.
  useEffect(() => {
    if (requestCue) showCueRef.current(requestCue.key);
  }, [requestCue]);
  // The page asked for a position (its phase box, a measure card): show it, unless it is already on show.
  const openPositionRef = useRef(openPosition);
  openPositionRef.current = openPosition;
  const dwellingLabelRef = useRef<string | null>(null);
  dwellingLabelRef.current = dwelling?.label ?? null;
  useEffect(() => {
    if (!requestPosition || dwellingLabelRef.current === requestPosition.label) return;
    const m = moments.find((x) => x.label === requestPosition.label);
    if (m) openPositionRef.current(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPosition]);
  const onPositionChangeRef = useRef(onPositionChange);
  onPositionChangeRef.current = onPositionChange;
  useEffect(() => {
    if (dwelling?.label) onPositionChangeRef.current?.(dwelling.label);
  }, [dwelling?.label]);
  const onActiveCueChangeRef = useRef(onActiveCueChange);
  onActiveCueChangeRef.current = onActiveCueChange;
  useEffect(() => {
    onActiveCueChangeRef.current?.(dwelling ? activeCue : null);
  }, [activeCue, dwelling]);
  useEffect(() => {
    if (coachingRows.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      e.preventDefault();
      showCueRef.current(stepCue(coachingRows, activeCueRef.current, e.key === "ArrowRight" ? 1 : -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [coachingRows]);

  // The current pose file frame — the cursor every chart beside the player follows.
  const cursorFrame = useMemo(() => {
    void panelTick;
    return frameIndexAt(relTime(videoRef.current?.currentTime ?? 0), fps, frames.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelTick, fps, frames.length]);

  // Every number below is the C# analysis pass's (liveSeries) — the same statics the rendered
  // pills print. The browser renders: charts in the page's own chart shape, tiles that read the
  // artifact's sample at the cursor. No artifact (404) = no panels, never a local recompute.
  const gaitPanel = useMemo(() => {
    if (!series || !isGait) return null;
    void panelTick;
    const charts: MetricChartData[] = [];
    if (series.cadenceSeries.length >= 2)
      charts.push(seriesChart("Cadence over time", "spm", series.cadenceSeries, series.minFrame, (x) => x.spm));
    if (series.kneeAtStrike && series.kneeAtStrike.perStrike.length >= 2)
      charts.push(
        seriesChart("R knee at foot strike", "°", series.kneeAtStrike.perStrike, series.minFrame, (x) => x.deg, "positionMeasures")
      );
    // The cadence reading at the cursor: the running cadence the pass computed at the last strike
    // reached so far; the whole-clip number before the first one.
    const idx = frameIndexAt(relTime(videoRef.current?.currentTime ?? 0), fps, frames.length);
    const started = idx > 0;
    let spm: number | null = series.cadenceSpm;
    if (started) {
      const reached = series.cadenceSeries.filter((x) => x.frame - series.minFrame <= idx);
      spm = reached.length > 0 ? reached[reached.length - 1].spm : null;
    }
    return { spm, strikes: series.strikes.length, knee: series.kneeAtStrike, charts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, isGait, panelTick, fps, frames.length]);

  // Body joint angles per side and joint, straight from the artifact (the reference product's
  // "Body Joint Angles": one big chart with a series picker, tiles per side that read at the cursor).
  const JOINT_LABELS: Record<string, string> = { knee: "Knee angle", hip: "Hip angle", elbow: "Elbow angle", shoulder: "Shoulder angle" };
  const joints = useMemo(() => {
    if (!series) return [] as Array<{ key: string; label: string; left: AngleSampleList; right: AngleSampleList }>;
    return Object.keys(JOINT_LABELS)
      .map((key) => ({
        key,
        label: JOINT_LABELS[key],
        left: series.jointAngles?.left?.[key] ?? [],
        right: series.jointAngles?.right?.[key] ?? [],
      }))
      .filter((j) => j.left.length > 0 || j.right.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series]);

  // The series picker: gait's own series first for gait, then the body joints, then the page's
  // charts. Values are stable keys so a re-render never loses the choice.
  const seriesOptions = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [];
    (gaitPanel?.charts ?? []).forEach((c, i) => out.push({ value: `gait:${i}`, label: c.title }));
    joints.forEach((j) => out.push({ value: `joint:${j.key}`, label: j.label }));
    pageCharts.forEach((c, i) => out.push({ value: `page:${i}`, label: c.title }));
    return out;
  }, [gaitPanel?.charts, joints, pageCharts]);
  const activeSeries =
    seriesPick && seriesOptions.some((o) => o.value === seriesPick) ? seriesPick : seriesOptions[0]?.value ?? null;

  const bigChart = (() => {
    if (!activeSeries) return null;
    const [kind, id] = activeSeries.split(":");
    if (kind === "gait") {
      const c = gaitPanel?.charts[Number(id)];
      return c ? <LiveAngleChart chart={c} index={0} cursorFrame={cursorFrame} extremes xTime={fps} tall /> : null;
    }
    if (kind === "page") {
      const c = pageCharts[Number(id)];
      return c ? <LiveAngleChart chart={c} index={1} cursorFrame={cursorFrame} extremes xTime={fps} tall /> : null;
    }
    const j = joints.find((x) => x.key === id);
    if (!j || !series) return null;
    const short = j.label.replace(/ angle$/i, "").toLowerCase();
    const primary = j.left.length > 0 ? { side: "Left", color: TRAIL_LEFT, samples: j.left } : { side: "Right", color: TRAIL_RIGHT, samples: j.right };
    const chart = seriesChart(j.label, "°", primary.samples, series.minFrame, (x) => x.deg);
    const other =
      j.left.length > 0 && j.right.length > 0
        ? [{ label: `Right ${short}`, color: TRAIL_RIGHT, dataPoints: seriesChart("", "°", j.right, series.minFrame, (x) => x.deg).dataPoints }]
        : [];
    return (
      <LiveAngleChart
        chart={chart}
        primaryLabel={`${primary.side} ${short}`}
        color={primary.color}
        extra={other}
        cursorFrame={cursorFrame}
        extremes
        xTime={fps}
        tall
      />
    );
  })();

  // Where the metrics go follows the canvas: a portrait canvas (gait) is a narrow column with
  // room beside it from md up; a landscape canvas (golf) wants the width, so the column stacks
  // underneath — except on large screens, where the live card spans the whole page row and a
  // column beside the stage leaves the stage bigger than stacking does.
  const landscape = stage.w > stage.h;
  const showPause = coachOn ? !!coachVideoUrl && coachPlaying : playing || (!!dwelling && walkState === "running");

  return (
    <div
      className={`flex-1 min-h-0 flex bg-black ${landscape ? "flex-col lg:flex-row" : "flex-col md:flex-row"}`}
      data-testid="live-free-analysis"
    >
      <div
        ref={playerRef}
        className={fullscreen && !nativeFullscreen ? "fixed inset-0 z-[100] bg-black" : "relative flex-1 min-h-0 bg-black"}
        data-fullscreen={fullscreen ? "true" : "false"}
        data-testid="live-player"
      >
        {/* The 16:9 STAGE — the stills' canvas shape. Playback lives inside it with a blurred
            cover backdrop and the renderer's watermark, so a still fading in is the SAME
            surface getting feedback burnt in, not a different-looking view (user review,
            2026-09-12: "the watermarks and sizing differ from the playback view"). */}
        <div
          ref={containerRef}
          className="absolute inset-x-0 top-0 flex items-center justify-center overflow-hidden bg-black"
          style={{ bottom: barHeight }}
          data-testid="live-stage-area"
        >
          {/* The stage is a VISIBLE, constant column in both states: the live backdrop is clipped
              to it exactly as the still's baked backdrop is, so when the still fades in only the
              column's CONTENTS change — its edges, size and position never move. (Measured on
              17816: a panel-wide backdrop made the column invisible during playback and then
              snap in as a hard-edged card with the still.) */}
          <div
            className="relative overflow-hidden bg-black"
            style={stageBox ? { width: stageBox.w, height: stageBox.h } : { visibility: "hidden", width: 1, height: 1 }}
            onClick={togglePlay}
            data-testid="live-stage"
          >
            <video
              ref={backdropRef}
              src={clipUrl}
              className="absolute inset-0 w-full h-full object-cover blur-xl brightness-[.55] scale-110"
              muted
              playsInline
              loop
              preload="auto"
              aria-hidden="true"
              data-testid="live-backdrop"
            />
            {/* The sharp window at the renderer's placement; the clip and the skeleton canvas
                share one rect inside it, scaled so the renderer's crop fills the window. */}
            <div className="absolute overflow-hidden" style={windowStyle} data-testid="live-placement">
              {/* max-w-none: Tailwind's preflight caps <video> at max-width:100%, which would pin
                  a crop-scaled clip to the window width while the canvas scales — measured on
                  17816 with a synthetic stage. */}
              <video
                ref={videoRef}
                src={clipUrl}
                className="absolute max-w-none"
                style={clipStyle}
                autoPlay={!held}
                muted
                playsInline
                loop
                preload="auto"
                onPlay={(e) => {
                  if (suspended || coachOnRef.current) {
                    e.currentTarget.pause();
                    return;
                  }
                  setPlaying(true);
                  backdropRef.current?.play().catch(() => undefined);
                }}
                onPause={() => {
                  setPlaying(false);
                  backdropRef.current?.pause();
                }}
                onEnded={() => setPlaying(false)}
                onLoadedData={() => setClipReady(true)}
                onCanPlay={() => setClipReady(true)}
                onLoadedMetadata={(e) => {
                  if (spanStart > 0 && e.currentTarget.currentTime < spanStart) e.currentTarget.currentTime = spanStart;
                }}
                data-testid="live-clip"
              />
              <canvas
                ref={canvasRef}
                width={poseFile.captureWidth || 720}
                height={poseFile.captureHeight || 1280}
                className="absolute pointer-events-none max-w-none"
                // No skeleton over a clip that has no frame yet (owner, 2026-10-12).
                style={{ ...clipStyle, visibility: clipReady ? "visible" : "hidden" }}
                data-testid="live-skeleton-canvas"
              />
            </div>
            {!dwelling && <RendererWatermark stamp={watermarkStamp} w={stage.w} h={stage.h} />}
            {!clipReady && !dwelling && (
              <div className="absolute inset-0 pointer-events-none" data-testid="live-clip-loading">
                {/* P1's frame, greyed and faded, behind the loader (owner, 2026-10-12) — never a bare skeleton. */}
                {loadingStillSrc && (
                  <img
                    src={loadingStillSrc}
                    alt=""
                    className="absolute max-w-none object-contain opacity-30 grayscale"
                    style={stillStyle}
                    data-testid="live-clip-loading-still"
                  />
                )}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-white/80">
                  <Loader2 className="h-8 w-8 animate-spin text-el-400" />
                  Loading your swing…
                </div>
              </div>
            )}
            {dwelling && stageStillSrc && (
              <div className="absolute inset-0 overflow-hidden">
                {underStill && underStill !== stageStillSrc && (
                  <img
                    src={underStill}
                    alt=""
                    className="absolute max-w-none object-contain"
                    style={stillStyle}
                    data-testid="live-dwell-still-under"
                  />
                )}
                <img
                  // A fresh element per frame: it starts invisible over the one underneath and fades in once loaded.
                  // (Reusing one element faded the new frame OUT to "not loaded" first, then back in: a flicker.)
                  key={stageStillSrc}
                  src={stageStillSrc}
                  alt={activeRowData ? `${dwelling.label} · ${activeRowData.description}` : `${dwelling.label} feedback`}
                  className={`absolute max-w-none object-contain ${loadedStill === stageStillSrc ? "live-still-in" : "opacity-0"}`}
                  style={stillStyle}
                  onLoad={() => setLoadedStill(stageStillSrc)}
                  onError={() => setLoadedStill(stageStillSrc)}
                  ref={(el) => {
                    if (el) lastStillRef.current = { src: stageStillSrc, alt: el.alt };
                  }}
                  data-testid="live-dwell-still"
                />
              </div>
            )}
            {!dwelling && leavingStill && (
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <img
                  src={leavingStill.src}
                  alt={leavingStill.alt}
                  className="absolute max-w-none object-contain live-still-leaving"
                  style={stillStyle}
                  data-testid="live-dwell-still-leaving"
                />
              </div>
            )}
            {dwelling && stageStillSrc && loadedStill !== stageStillSrc && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="live-dwell-loading">
                <Loader2 className="h-8 w-8 animate-spin text-white/80" />
              </div>
            )}
            {dwelling && !stageStillSrc && framesPending && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-sm text-white/85"
                data-testid="live-frame-pending"
              >
                <Loader2 className="h-8 w-8 animate-spin" />
                Preparing {dwelling.label}…
              </div>
            )}
            {dwelling && ((voicePending && !voice) || waitingVoice) && (
              // Centred on the stage (owner, 2026-10-15); it never takes a click meant for the frame.
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white/90"
                  data-testid="live-voice-pending"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Coaching voice loading
                </div>
              </div>
            )}
            {dwelling && voice && voiceBlocked && (
              // A clear, centred prompt (owner, 2026-10-18 onboarding plan): browsers only let a page speak after a tap.
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVoiceBlocked(false);
                    playVoice(voicingRef.current ?? voiceFor(activeCueRef.current));
                  }}
                  className="pointer-events-auto flex items-center gap-2 rounded-full bg-el-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-el-700"
                  data-testid="live-voice-tap"
                >
                  <Volume2 className="h-4 w-4" />
                  Tap to hear your coaching
                </button>
              </div>
            )}
            <audio
              ref={voiceRef}
              preload="auto"
              muted={muted}
              className="hidden"
              data-testid="live-voice"
              // Sound is coming out, so nothing is blocked: the tap prompt never sits over a voice that is speaking.
              onPlaying={() => { setSpeaking(true); setVoiceBlocked(false); }}
              onPause={() => setSpeaking(false)}
              onEnded={() => setSpeaking(false)}
            />
            {showingAllCues && (
              <div
                className="absolute top-2 right-2 rounded bg-black/70 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.06em] text-white/85"
                data-testid="live-still-all-cues"
                title="This upload was analysed before each cue had its own picture — re-render it to split them"
              >
                All cues at {dwelling!.label}
              </div>
            )}
            {/* Coaching: the narrated video IN the stage, the same frame the swing and the stills play in, so switching
                between Coaching and a position never moves the picture's edges (owner, 2026-11-01: "the sizing are
                different between the video playback and the individual feedbacks"). Kept loaded when a position is
                picked so Coaching carries on from where it was. A click on it reaches the stage's own toggle. */}
            {coachVideoUrl && (
              <video
                ref={coachRef}
                src={coachVideoUrl}
                className={`absolute inset-0 z-20 h-full w-full object-contain bg-black ${coachOn ? "" : "hidden"}`}
                playsInline
                preload="auto"
                poster={loadingStillSrc ?? undefined}
                onPlay={(e) => {
                  setCoachPlaying(true);
                  if (e.currentTarget.readyState < 3) setCoachLoading(true);
                }}
                onPlaying={() => setCoachLoading(false)}
                onWaiting={() => setCoachLoading(true)}
                onPause={() => { setCoachPlaying(false); setCoachLoading(false); }}
                onEnded={() => { setCoachPlaying(false); setCoachLoading(false); }}
                data-testid="live-coach-video"
              />
            )}
            {coachOn && coachVideoUrl && coachLoading && (
              <div
                className="absolute inset-0 z-[25] flex flex-col items-center justify-center gap-2 bg-black/40 text-sm text-white/90 pointer-events-none"
                data-testid="live-coach-loading"
              >
                <Loader2 className="h-8 w-8 animate-spin text-el-400" />
                Loading your coaching…
              </div>
            )}
            {coachOn && coachVideoUrl && coachSilent && !coachLoading && (
              <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCoachSilent(false);
                    const c = coachRef.current;
                    if (c) { c.muted = muted; c.play?.()?.catch?.(() => undefined); }
                  }}
                  className="pointer-events-auto flex items-center gap-2 rounded-full bg-el-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-el-700"
                  data-testid="live-coach-tap"
                >
                  <Volume2 className="h-4 w-4" />
                  Tap to hear your coaching
                </button>
              </div>
            )}
          </div>
          {caption && captionsOn && !coachOn && (
            // Across the whole player, not inside the stage (owner, 2026-10-12: "the captions look huge on a phone"):
            // a portrait clip leaves the stage a narrow column there, and a caption squeezed into it ran one word a
            // line down the athlete. Below the page's tab strip (Feedback / Analysis video / 3D) unless full screen.
            <div className={`absolute inset-x-0 ${fullscreen ? "top-3" : "top-11"} flex justify-center px-3 pointer-events-none`}>
              <div
                key={caption}
                className="live-caption line-clamp-3 max-w-[min(92%,36rem)] rounded-xl bg-white/95 px-2.5 py-1 text-center text-[11px] sm:text-xs md:text-sm font-medium leading-snug text-slate-900 shadow-md"
                data-testid="live-caption"
              >
                {caption}
              </div>
            </div>
          )}
          {/* Free: the Coaching pill is the upgrade page, the swing faint behind it (owner, 2026-11-01: "make it more
              salesy and popping"). Opened by itself, a bar counts down the time before the swing plays. */}
          {coachOn && !coachVideoUrl && coachOffer && (
            <div
              ref={setOfferEl}
              className={`absolute inset-0 z-20 flex items-center justify-center bg-black/75 ${offerCompact ? "p-2" : "p-4"}`}
              onClick={(e) => e.stopPropagation()}
              data-testid="live-coach-offer"
            >
              <div className={`relative w-full max-w-sm overflow-hidden rounded-2xl bg-gradient-to-br from-el-600/40 via-slate-900/95 to-slate-950 ${offerCompact ? "p-4" : "p-5"} text-left text-white shadow-[0_0_48px_rgba(16,185,129,0.45)] ring-1 ring-el-400/50`}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-el-400/25 blur-3xl" />
                <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-slate-900 shadow">
                  <Sparkles className="h-3 w-3" />
                  Premium
                </span>
                <div className={offerCompact ? "mt-2 text-lg font-extrabold leading-tight" : "mt-3 text-xl font-extrabold leading-tight sm:text-2xl"}>
                  {coachOffer.headline}
                </div>
                {!offerCompact && <div className="mt-1.5 text-sm text-white/75">{coachOffer.text}</div>}
                {!offerCompact && (
                <ul className="mt-4 space-y-2">
                  {coachOffer.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm font-medium text-white/90">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-el-500 text-white">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
                )}
                <button
                  type="button"
                  className={`${offerCompact ? "mt-3 py-2.5" : "mt-5 py-3"} flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-el-400 to-el-600 text-sm font-bold text-white shadow-lg shadow-el-500/40 ring-1 ring-white/20 transition hover:brightness-110 active:scale-[0.98]`}
                  onClick={(e) => { e.stopPropagation(); coachOffer.onClick(); }}
                  data-testid="live-coach-offer-cta"
                >
                  {coachOffer.label}
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className={`${offerCompact ? "mt-2" : "mt-3"} block w-full text-center text-xs text-white/55 hover:text-white`}
                  onClick={(e) => { e.stopPropagation(); leaveCoach(true); }}
                  data-testid="live-coach-offer-skip"
                >
                  Not now, watch the free analysis
                </button>
                {offerFirstRef.current && (
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-white/10">
                    <div
                      className="coach-offer-countdown h-1 bg-el-400"
                      style={{ animationDuration: `${COACH_OFFER_MS}ms` }}
                      data-testid="live-coach-offer-countdown"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* No centred play badge while paused (user, 2026-09-19: "it blocks the view") — it sat
            dead over the athlete, the one thing the view exists to show. Resuming never depended
            on it: the stage toggles play on click and the control bar below carries Play/Pause. */}
        <div ref={barRef} className="absolute bottom-0 inset-x-0" data-testid="live-control-bar">
          {/* The timelines: a track you can see and a thumb you can grab, with room either side so the thumb is never
              half off the edge (owner, 2026-11-01: "why is there no slider for the timeline when doing the coaching
              playback?"). The fill and thumb are written per frame by the loops, not by React. */}
          {coachOn && coachVideoUrl && (
            <div className="relative h-5 mx-3 flex items-center group" data-testid="live-coach-timeline">
              <div className="absolute inset-x-0 h-1 group-hover:h-1.5 transition-[height] rounded-full bg-white/25 overflow-hidden">
                <div ref={coachFillRef} className="h-full bg-el-500" style={{ width: "0%" }} data-testid="live-coach-fill" />
              </div>
              <input
                ref={coachScrubRef}
                type="range"
                min={0}
                max={SCRUB_STEPS}
                step={1}
                defaultValue={0}
                onInput={(e) => scrubCoach(Number((e.target as HTMLInputElement).value))}
                onChange={(e) => scrubCoach(Number(e.target.value))}
                onPointerDown={() => { coachScrubbingRef.current = true; }}
                onPointerUp={endCoachScrub}
                onPointerCancel={endCoachScrub}
                onKeyUp={endCoachScrub}
                aria-label="Coaching video timeline"
                data-testid="live-coach-scrub"
                className="live-scrub absolute inset-x-0 w-full h-5 m-0 bg-transparent appearance-none cursor-pointer"
              />
            </div>
          )}
          <div className={`relative h-5 mx-3 flex items-center group ${coachOn && coachVideoUrl ? "hidden" : ""}`}>
            <div className="absolute inset-x-0 h-1 group-hover:h-1.5 transition-[height] rounded-full bg-white/25 overflow-hidden">
              <div ref={progressRef} className="h-full bg-el-500" style={{ width: "0%" }} />
            </div>
            <input
              ref={scrubRef}
              type="range"
              min={0}
              max={SCRUB_STEPS}
              step={1}
              defaultValue={0}
              onInput={(e) => scrubTo(Number((e.target as HTMLInputElement).value))}
              onChange={(e) => scrubTo(Number(e.target.value))}
              onPointerDown={() => { scrubbingRef.current = true; }}
              onPointerUp={() => { scrubbingRef.current = false; }}
              onPointerCancel={() => { scrubbingRef.current = false; }}
              onKeyUp={() => { scrubbingRef.current = false; }}
              aria-label="Timeline"
              data-testid="live-scrub"
              className="live-scrub absolute inset-x-0 w-full h-5 m-0 bg-transparent appearance-none cursor-pointer"
            />
          </div>
          <div className="flex flex-nowrap items-center gap-1 px-2 py-2 bg-black/60" data-testid="live-button-row">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={showPause ? "Pause" : "Play"}
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
            >
              {showPause ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => {
                if (coachOnRef.current && coachVideoUrl && coachRef.current) {
                  coachRef.current.currentTime = 0;
                  coachRef.current.play()?.catch(() => undefined);
                  return;
                }
                leaveCoach();
                const video = videoRef.current;
                if (!video) return;
                consumedRef.current.clear();
                video.currentTime = spanStart;
                video.play().catch(() => undefined);
              }}
              aria-label="Replay"
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            {/* One frame back / forward (owner, 2026-10-12: "the arrow buttons should go forward + back of the
                playback"); the arrow KEYS still step the cues. */}
            {!coachOn && (<>
            <button
              type="button"
              onClick={() => stepFrame(-1)}
              aria-label="Back one frame"
              data-testid="live-step-back"
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => stepFrame(1)}
              aria-label="Forward one frame"
              data-testid="live-step-forward"
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            </>)}
            <span
              ref={timeRef}
              className="shrink-0 px-1 text-[0.7rem] font-medium tabular-nums text-white/75"
              aria-label="Time"
              data-testid="live-time"
            />
            {(voice || coachVideoUrl) && (
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? "Unmute feedback" : "Mute feedback"}
                aria-pressed={muted}
                data-testid="live-mute-toggle"
                className="ml-auto shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
                title={muted ? "Unmute the coaching voice" : "Mute the coaching voice"}
              >
                {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              </button>
            )}
            {/* The swing's own toggles: in Coaching they would do nothing, so they are not shown there (owner, 2026-11-01:
                "test all of these buttons and make sure they work in a good UX"). */}
            {!coachOn && (<>
            <button
              type="button"
              onClick={toggleAutoPause}
              aria-pressed={autoPause}
              aria-label={autoPause ? "Pauses on feedback" : "Plays through"}
              data-testid="live-autopause-toggle"
              className={`${voice || coachVideoUrl ? "" : "ml-auto "}shrink-0 h-6 w-6 rounded flex items-center justify-center transition ${
                autoPause ? "bg-el-500/25 text-el-300 hover:bg-el-500/35" : "bg-white/10 text-white/90 hover:bg-white/20"
              }`}
              title={autoPause ? "Pauses on each position to show its feedback — click to play straight through" : "Plays straight through — click to pause on each position's feedback"}
            >
              {autoPause ? <CirclePause className="h-3.5 w-3.5" /> : <FastForward className="h-3.5 w-3.5" />}
              <span className="sr-only">{autoPause ? "Pauses on feedback" : "Plays through"}</span>
            </button>
            <button
              type="button"
              onClick={toggleCaptions}
              aria-label={captionsOn ? "Hide captions" : "Show captions"}
              aria-pressed={!captionsOn}
              data-testid="live-captions-toggle"
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
              title={captionsOn ? "Hide the coaching captions" : "Show the coaching captions"}
            >
              {captionsOn ? <Captions className="h-3.5 w-3.5" /> : <CaptionsOff className="h-3.5 w-3.5" />}
            </button>
            </>)}
            <button
              type="button"
              onClick={togglePanel}
              aria-label={panelOpen ? "Hide coaching and metrics" : "Show coaching and metrics"}
              aria-pressed={panelOpen}
              data-testid="live-panel-toggle"
              className={`${voice || coachVideoUrl || !coachOn ? "" : "ml-auto "}shrink-0 h-6 w-6 rounded flex items-center justify-center transition ${
                panelOpen ? "bg-white/10 text-white/90 hover:bg-white/20" : "bg-el-500/25 text-el-300 hover:bg-el-500/35"
              }`}
              title={panelOpen ? "Hide coaching and metrics" : "Show coaching and metrics"}
            >
              {panelOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={fullscreen ? "Exit full screen" : "Full screen"}
              data-testid="live-fullscreen-toggle"
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
              title={fullscreen ? "Exit full screen" : "Full screen"}
            >
              {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          </div>
          {/* Coaching and the positions: a pill list under the buttons that wraps onto another line rather than
              scrolling or squeezing, so every pill shows at its full size (owner, 2026-11-01: "it still struggles to be
              easy to scroll horizontally", "it can work with the pills being on another row underneath", "make it be
              more like a pill list"). The same pills show in both modes, so the bar's height does not change between
              Coaching and a position. */}
          <div
            className="flex flex-wrap items-center gap-1.5 px-2 pb-2 bg-black/60"
            data-testid="live-position-pills"
          >
          {/* Coaching first: the narrated video, or for a free analysis the upgrade page (owner, 2026-11-01: "have the
              coach video be another pill on the timeline as a coaching button"). */}
          {hasCoach && (
            <button
              type="button"
              onClick={() => (coachOn ? togglePlay() : enterCoach())}
              aria-pressed={coachOn}
              data-active={coachOn ? "true" : undefined}
              data-testid="live-coach-pill"
              className={`shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[0.7rem] font-semibold transition ${
                coachOn ? "bg-el-500 text-white" : "bg-white text-el-700 hover:bg-el-50"
              }`}
            >
              <Play className="h-3 w-3" />
              Coaching
            </button>
          )}
          {/* Every position of the swing, in order (owner, 2026-10-12: "p5 + p8 ... grey boxes in the p position
              list"): one the server returned no feedback for is a grey box, there to show its place. */}
          {positionSlots.map((slot) => {
            const m = slot.moment;
            if (!m)
              return (
                <span
                  key={slot.label}
                  className="shrink-0 px-2.5 py-1 rounded-full text-[0.7rem] font-semibold bg-white/5 text-white/30 ring-1 ring-inset ring-white/10 cursor-default"
                  title={`No feedback at ${slot.label}`}
                  aria-disabled="true"
                  data-testid={`live-position-empty-${slot.label}`}
                >
                  {slot.label}
                </span>
              );
            return (
              <button
                key={m.label}
                type="button"
                onClick={() => openPosition(m)}
                data-active={!coachOn && dwelling?.label === m.label ? "true" : undefined}
                className={`shrink-0 px-2.5 py-1 rounded-full text-[0.7rem] font-semibold transition ${
                  !coachOn && dwelling?.label === m.label
                    ? "bg-el-500 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/20"
                }`}
              >
                {m.label}
              </button>
            );
          })}
          </div>
        </div>
      </div>

      {/* The metrics column: the SAME white chart cards the session page renders at the
          bottom, on the platform's light surface — NOT a custom dark panel "inside the video"
          (user review, 2026-09-12). Structure borrowed from the reference product (user,
          2026-09-12: "learn from their design from a ux perspective" — our colours stay): one
          big chart with a series picker, then tiles that read at the cursor. */}
      {/* Present unless the viewer folded it away: it holds the tabs, and Metrics says so when the analysis pass has
          not produced the artifact yet. */}
      {panelOpen && (
        <div
          className={
            landscape
              ? "shrink-0 w-full max-h-[48%] lg:max-h-none lg:w-[38%] lg:max-w-[560px] bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-200 p-3 flex flex-col gap-3 overflow-auto"
              : "shrink-0 w-full max-h-[45%] md:max-h-none md:w-1/2 md:max-w-[600px] bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200 p-3 flex flex-col gap-3 overflow-auto"
          }
          ref={sideRef}
          data-testid="live-side-charts"
          data-cursor-frame={cursorFrame}
        >
          {/* Pinned while the list scrolls under it, so the tabs, Auto-scroll and the fold are always to hand. */}
          <div className="sticky -top-3 z-10 -mx-3 -mt-3 px-3 pt-3 pb-2 bg-slate-50/95 backdrop-blur flex items-center justify-between gap-2" data-testid="live-tabs-row">
            <div className="inline-flex rounded-md bg-slate-100 p-0.5" role="tablist">
              {(["coaching", "metrics"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setPickedTab(t)}
                  data-testid={`live-tab-${t}`}
                  className={`px-3 py-1 text-xs font-medium rounded transition ${
                    tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t === "coaching" ? "Coaching" : "Metrics"}
                </button>
              ))}
            </div>
            <button
              type="button"
              role="switch"
              onClick={toggleFollow}
              aria-pressed={followOn}
              aria-checked={followOn}
              data-testid="live-autoscroll-toggle"
              title={followOn ? "The list follows each position's feedback as it plays — click to stop" : "Click to have the list follow each position's feedback as it plays"}
              className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              <span className={`relative h-3.5 w-6 rounded-full transition ${followOn ? "bg-el-500" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow transition-all ${followOn ? "left-3" : "left-0.5"}`} />
              </span>
              Auto-scroll
            </button>
            <button
              type="button"
              onClick={togglePanel}
              aria-label="Hide coaching and metrics"
              title="Hide coaching and metrics"
              data-testid="live-panel-collapse"
              className="shrink-0 h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            >
              {landscape ? <PanelRightClose className="h-4 w-4 hidden lg:block" /> : <PanelRightClose className="h-4 w-4 hidden md:block" />}
              {landscape ? <PanelBottomClose className="h-4 w-4 lg:hidden" /> : <PanelBottomClose className="h-4 w-4 md:hidden" />}
            </button>
          </div>
          {tab === "metrics" && seriesOptions.length > 0 && (
            <label className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.14em] text-slate-500">
              Series
              <select
                value={activeSeries ?? ""}
                onChange={(e) => setSeriesPick(e.target.value)}
                className="flex-1 min-w-0 normal-case tracking-normal text-xs text-slate-800 bg-white border border-slate-200 rounded px-2 py-1"
                aria-label="Series"
                data-testid="live-series-picker"
              >
                {seriesOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {tab === "coaching" && (
            <>
              {(coaching?.summary || coaching?.total) && (
                <div className="rounded-md bg-white border border-slate-100 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-el-600" />
                    <div className="text-sm font-semibold text-slate-900">Summary</div>
                    {coaching?.total && (
                      <span className="ml-auto text-xs font-semibold text-el-700">Total {coaching.total}</span>
                    )}
                  </div>
                  {coaching?.summary && <p className="text-xs leading-relaxed text-slate-600">{coaching.summary}</p>}
                </div>
              )}
              {coachingRows.length > 0 ? (
                <ul className="space-y-2" data-testid="live-coaching-list">
                  {coachingRows.map((r) => {
                    // ONE cue lights up — the one on the stage — never every row of its position. It opens like an
                    // accordion with what the coach says about it; the others stay one line (owner, 2026-10-21: the
                    // separate "Your coaching" list below the video merged into this one).
                    const isActive = activeCue === r.key;
                    const said = (voice?.items ?? []).filter((it) => it.position === r.pLabel && it.overlay === r.overlay);
                    const voiceState: "none" | "generating" | "ready" | "speaking" =
                      said.length === 0 ? "none"
                        : said.some((it) => !it.audio) ? "generating"
                        : isActive && speaking ? "speaking"
                        : "ready";
                    return (
                      <li key={r.key}>
                        <button
                          type="button"
                          data-testid="live-coaching-row"
                          data-cue={r.key}
                          data-active={isActive ? "true" : "false"}
                          data-open={isActive ? "true" : "false"}
                          data-voice={voiceState}
                          aria-expanded={isActive}
                          onClick={() => showCue(r.key)}
                          className={`w-full text-left rounded-md transition px-3 py-2 border ${
                            isActive
                              ? "bg-el-50 border-el-300 ring-1 ring-el-200"
                              : "bg-white border-slate-100 hover:border-el-200"
                          }`}
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <div className="flex items-baseline gap-2 min-w-0">
                              <span className="text-[0.65rem] font-semibold text-el-700 shrink-0 tabular-nums">
                                {r.pLabel}
                              </span>
                              <div className="min-w-0">
                                <div className={`text-xs truncate ${isActive ? "font-semibold text-el-800" : "font-medium text-slate-800"}`}>
                                  {r.description}
                                </div>
                                {r.subText && (
                                  <div className={`text-[0.6rem] text-slate-400 ${isActive ? "" : "truncate"}`}>{r.subText}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {r.values?.map((v) => (
                                <span
                                  key={v}
                                  className={`rounded px-1.5 py-0.5 text-[0.6rem] font-semibold tabular-nums ${
                                    isActive ? "bg-el-100 text-el-800" : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {v}
                                </span>
                              ))}
                              {voiceState === "generating" && (
                                <span title="Coaching voice loading" data-testid="live-coaching-voice-generating">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                                </span>
                              )}
                              {voiceState === "ready" && (
                                <span title="Coaching voice ready" data-testid="live-coaching-voice-ready">
                                  <Volume2 className="h-3.5 w-3.5 text-slate-400" />
                                </span>
                              )}
                              {voiceState === "speaking" && (
                                <span title={muted ? "Speaking (muted)" : "Speaking"} data-testid="live-coaching-voice-speaking">
                                  {muted ? (
                                    <VolumeX className="h-3.5 w-3.5 text-el-600" />
                                  ) : (
                                    <Volume2 className="h-3.5 w-3.5 animate-pulse text-el-600" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          {isActive && said.length > 0 && (
                            <div className="mt-2 border-t border-el-100 pt-2 text-xs leading-relaxed text-slate-700" data-testid="live-coaching-text">
                              {said.map((it) => it.text).join(" ")}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-xs text-slate-500">No coaching feedback for this clip yet.</div>
              )}
            </>
          )}

          {tab === "metrics" && (
            <>
          {!liveSeries && (
            <div className="rounded-md bg-white border border-slate-100 p-3 text-xs text-slate-500" data-testid="live-metrics-pending">
              Live metrics are produced by the analysis pass and are not available for this clip yet.
            </div>
          )}
          {liveSeries && !series && (
            <div className="rounded-md bg-white border border-slate-100 p-3 text-xs text-slate-500" data-testid="live-metrics-stale">
              Live metrics for this clip were computed at a different frame rate and need re-running by the analysis pass.
            </div>
          )}
          {bigChart}

          {gaitPanel && (
            <div data-testid="live-gait-panel">
              <div className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-500 mb-1">Gait</div>
              <div className="grid gap-2 grid-cols-3">
                <Tile
                  value={gaitPanel.spm != null ? `${Math.round(gaitPanel.spm)} spm` : "—"}
                  label="Cadence"
                  testid="live-cadence-spm"
                />
                <Tile value={`${gaitPanel.strikes}`} label="Strikes" />
                <Tile
                  value={gaitPanel.knee ? `${Math.round(gaitPanel.knee.meanDeg)}° ±${Math.round(gaitPanel.knee.spreadDeg)}°` : "—"}
                  label="R knee @ strike"
                  testid="live-knee-at-strike"
                />
              </div>
              <div className="text-[0.65rem] text-slate-400 mt-1">
                <span style={{ color: TRAIL_LEFT }}>●</span> left ·{" "}
                <span style={{ color: TRAIL_RIGHT }}>●</span> right ankle path
              </div>
            </div>
          )}

          {joints.length > 0 &&
            (["right", "left"] as const).map((side) => (
              <div key={side}>
                <div className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-500 mb-1">
                  <span style={{ color: side === "left" ? TRAIL_LEFT : TRAIL_RIGHT }}>●</span> {side} body
                </div>
                <div className="grid gap-2 grid-cols-2">
                  {joints.map((j) => {
                    const sample = series ? sampleAtIndex(j[side], series.minFrame, cursorFrame) : null;
                    return (
                      <Tile
                        key={j.key}
                        value={sample ? `${Math.round(sample.deg)}°` : "—"}
                        label={j.label}
                        testid={`live-angle-tile-${side}-${j.key}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
